import { FC, ChangeEvent, ReactNode } from 'react';
import {
  Select as MuiSelect,
  MenuItem,
  ListItemIcon,
  FormControl,
  Box,
  SelectClassKey,
} from '@material-ui/core';
import { ClassNameMap } from '@material-ui/styles';
import type { FormControlProps } from '@material-ui/core/FormControl';
import { ArrowDownSmallIcon, WellDone } from '@images/icons';
import { Option } from '@types';

import './index.scss';

interface ISelect extends FormControlProps {
  value: string | number | null;
  options: Option<string | number>[];
  handleChange: (value: string | number) => void;
  disabled?: boolean;
  placeholder?: string;
  error?: boolean;
  renderButton?: () => JSX.Element;
  customClass?: string;
  testId?: string;
  classes?: Partial<ClassNameMap<SelectClassKey>>;
}

const renderOptionLabel = (optionLabel?: string): ReactNode | string | undefined =>
  optionLabel === 'None' ? <span className="empty-value">{optionLabel}</span> : optionLabel;

export const Select: FC<ISelect> = ({
  value,
  placeholder = '',
  options,
  handleChange,
  disabled = false,
  error = false,
  renderButton,
  customClass,
  testId = 'select',
  classes,
  ...properties
}) => {
  return (
    <FormControl {...properties} disabled={disabled} fullWidth>
      <MuiSelect
        classes={classes}
        data-testid={testId}
        onChange={(
          event: ChangeEvent<{
            name: string;
            value: string;
          }>
        ) => {
          handleChange(event.target.value);
        }}
        value={value || ''}
        renderValue={(selected) => {
          const selectedOptionLabel = options.find((option) => option.value === selected)?.label;

          return renderOptionLabel(selectedOptionLabel) ?? placeholder;
        }}
        {...(placeholder && !value && { className: 'Mui-placeholder' })}
        IconComponent={ArrowDownSmallIcon}
        displayEmpty
        fullWidth
        error={error}
        MenuProps={{
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
          getContentAnchorEl: null,
          disableScrollLock: true,
        }}
      >
        {options.map((option) => (
          <MenuItem
            data-testid={option?.value ?? option.label.toLowerCase()}
            className={customClass}
            key={option.value}
            value={option.value}
            disabled={option.disabled}
          >
            <ListItemIcon>{value === option.value && <WellDone />}</ListItemIcon>
            <Box component="span">{renderOptionLabel(option.label)}</Box>
          </MenuItem>
        ))}
        {renderButton && renderButton()}
      </MuiSelect>
    </FormControl>
  );
};
